// pages/LinkedInPostEdit.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/LinkedInPostEdit.css';

const LinkedInPostEdit = () => {
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // Get ID from URL
  const id = window.location.pathname.split('/').pop();

  const fetchPost = async () => {
    try {
      const response = await fetch('https://n8n.iniai.cloud/webhook/get-linkedin-posts-all', {
        headers: {
          'x-api-key': 'auth_k_2f9a8b3c7d6e4f5g9h8i1j2k3l4m5n6'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch post');
      const posts = await response.json();
      const foundPost = posts.find(p => p['Content Request ID'].toString() === id);
      
      if (!foundPost) throw new Error('Post not found');
      
      setPost(foundPost);
      setEditedContent(foundPost['Generated Content']);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPost();
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch('https://n8n.iniai.cloud/webhook/edit-linkedin-post', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': 'auth_k_2f9a8b3c7d6e4f5g9h8i1j2k3l4m5n6'
        },
        body: JSON.stringify({
          contentId: id,
          content: editedContent,
          editedBy: user.name
        })
      });

      if (!response.ok) throw new Error('Failed to save changes');

      setSaveMessage('Post updated successfully!');
      // Refresh post data
      await fetchPost();
      setSaveMessage(null);
    } catch (err) {
      setError('Failed to save changes: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading-state">Loading post...</div>;
  if (error) return <div className="error-state">{error}</div>;
  if (!post) return <div className="error-state">Post not found</div>;

  return (
    <div className="post-edit-container">
      <h1>Edit LinkedIn Post</h1>

      <div className="page-navigation">
        <div className="breadcrumb">
          All Posts / Edit Post
        </div>
        <a href="/linkedinpostsall" className="back-link">← Back to Posts</a>
      </div>

      <div className="meta-section">
        <div className="meta-row">
          <span className="meta-label">Author:</span>
          <span className="meta-value">{post['Name (from Writer ID)']}</span>
          
          <span className="meta-label">Status:</span>
          <span className={`status-badge ${post.Status.toLowerCase()}`}>{post.Status}</span>
          
          <span className="meta-label">Validation Score:</span>
          <span className="score">{post['Style Validation Score']}</span>
        </div>

        <div className="meta-row dates">
          <div className="date-group">
            <span className="meta-label">Generated:</span>
            <span className="meta-value">{new Date(post['Generated Date']).toLocaleDateString()}</span>
          </div>

          {post['Edited Date'] && (
            <div className="date-group">
              <span className="meta-label">Last Edited:</span>
              <span className="meta-value">
                {new Date(post['Edited Date']).toLocaleDateString()}
                {post['Edited By'] && <span className="edited-by"> by {post['Edited By']}</span>}
              </span>
            </div>
          )}
        </div>
      </div>

      {post['Style Suggestions'] && (
        <div className="style-suggestions">
          <h2>Style Suggestions</h2>
          <ul className="suggestions-list">
            {post['Style Suggestions'].split('. ').map((suggestion, index) => (
              <li key={index} className="suggestion-item">
                {suggestion}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="content-sections">
        <div className="content-section">
          <h2>Original Content</h2>
          <div className="content-box original">
            {post['Generated Content']}
          </div>
        </div>

        <div className="content-section">
          <h2>Edit Content</h2>
          <textarea
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            className="content-box edit-textarea"
            placeholder="Edit your content here..."
          />
        </div>
      </div>

      {saveMessage && (
        <div className="save-message success">
          {saveMessage}
        </div>
      )}

      <div className="button-group">
        <button 
          onClick={() => window.location.href = '/linkedinpostsall'}
          className="cancel-button"
          disabled={saving}
        >
          Cancel
        </button>
        <button 
          onClick={handleSave}
          className="save-button"
          disabled={saving || editedContent === post['Generated Content']}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default LinkedInPostEdit;